import { Factory } from 'fishery'
import { type OpalApiResponseDocument } from '@opallabs/api'
import { workflowResourceFactory } from '../resource_objects/workflow'
import { workflowContextResourceFactory } from '../resource_objects/workflow_context'
import { workflowStageResourceFactory } from '../resource_objects/workflow_stage'

type ReadWorkflowsResponseV3 = OpalApiResponseDocument<'ReadWorkflowsV3', 200>
type Progress =
  ReadWorkflowsResponseV3['data'][number]['attributes']['progress']
type StageType = 'approval' | 'circuit_breaker' | 'task'

type ReadEntityWorkflowsTransientParams = {
  entityType: 'block' | 'moment'
  entityIds: string[]
  /** @default 'v1' */
  version: string
  /** @default 'tasks_pending' */
  progress: Progress
  /** @default 'Stage <entity id>' */
  stageTitle?: string
}

const stageTypeOf = (progress: Progress): StageType => {
  switch (progress) {
    case 'approval_pending':
    case 'declined':
    case 'approved':
      return 'approval'
    case 'paused':
      return 'circuit_breaker'
    default:
      return 'task'
  }
}

/**
 * One workflow per block or moment, with the id
 * `workflow-<entity id>-<version>` and a context `context-<entity id>`. Unless
 * `progress` is `not_started`, each has a current stage
 * `stage-<entity id>-<version>`.
 */
export const readEntityWorkflowsFactory: Factory<
  ReadWorkflowsResponseV3,
  ReadEntityWorkflowsTransientParams
> = Factory.define<ReadWorkflowsResponseV3, ReadEntityWorkflowsTransientParams>(
  ({
    transientParams: {
      entityType = 'block',
      entityIds = [],
      version = 'v1',
      progress = 'tasks_pending',
      stageTitle,
    },
  }) => {
    const built = entityIds.map((entityId) => {
      const workflowId = `workflow-${entityId}-${version}`
      const context = workflowContextResourceFactory.build({
        id: `context-${entityId}`,
        relationships: {
          block: {
            data:
              entityType === 'block' ? { id: entityId, type: 'block' } : null,
          },
          moment: {
            data:
              entityType === 'moment' ? { id: entityId, type: 'moment' } : null,
          },
          workflow: { data: { id: workflowId, type: 'workflow' } },
        },
      })
      const stage =
        progress === 'not_started'
          ? undefined
          : workflowStageResourceFactory.build({
              id: `stage-${entityId}-${version}`,
              attributes: {
                stage_type: stageTypeOf(progress),
                status:
                  progress === 'approved' || progress === 'tasks_complete'
                    ? 'complete'
                    : 'active',
                metadata: { title: stageTitle ?? `Stage ${entityId}` },
              },
              relationships: {
                workflow: { data: { id: workflowId, type: 'workflow' } },
              },
            })
      const workflow = workflowResourceFactory.build({
        id: workflowId,
        attributes: { progress, is_approved: progress === 'approved' },
        relationships: {
          context: { data: { id: context.id, type: 'workflow_context' } },
          current_stage: {
            data: stage ? { id: stage.id, type: 'workflow_stage' } : null,
          },
        },
      })
      return { workflow, included: stage ? [context, stage] : [context] }
    })

    return {
      data: built.map(({ workflow }) => workflow),
      included: built.flatMap(({ included }) => included),
      meta: { resource_type: 'workflow', total: built.length },
      links: {
        self: '/v3/workflows',
        first: '/v3/workflows',
        prev: null,
        next: null,
        last: '/v3/workflows',
      },
    } satisfies ReadWorkflowsResponseV3
  }
)
